import { McpAccessLevel, PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAccount } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { SettingsFormSkeleton } from '@/components/settings/settings-form-skeleton';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { getSitemateStartAccountQueryOptions, sitemateStartAccountKeys } from '@/queries/account';
import { getWorkspaceQueryOptions, workspaceKeys } from '@/queries/workspaces';
import { ApiService } from '@/services/api-v2';
import { accountService } from '@/services/global-api/account.service';
import { useUserStore } from '@/stores/user.store';

const formSchema = z.object({
  ssoEnabled: z.boolean(),
});

const mcpAccessFormSchema = z.object({
  mcpEnabled: z.boolean(),
});

type McpAccessFormData = z.infer<typeof mcpAccessFormSchema>;

function McpAccessToggleForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { org, setOrg } = useUserStore();
  const { canEditWorkspaceSettings } = usePermissions();

  // Source the toggle from a fresh workspace fetch rather than the persisted
  // `org` store, which can be rehydrated stale (app-bootstrap keeps a cached
  // workspace without refetching). Falls back to `org` until the query resolves.
  const { data: workspace } = useQuery(getWorkspaceQueryOptions({ workspaceId: org?.id }));
  const mcpAccessLevel = workspace?.mcpAccessLevel ?? org?.mcpAccessLevel ?? McpAccessLevel.None;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useForm<McpAccessFormData>({
    resolver: zodResolver(mcpAccessFormSchema),
    values: { mcpEnabled: mcpAccessLevel !== McpAccessLevel.None },
  });

  const { mutateAsync: updateMcpAccess, isPending } = useMutation({
    mutationFn: async (mcpEnabled: boolean) => {
      if (!org) {
        throw new Error('Workspace is not loaded');
      }
      const nextLevel = mcpEnabled ? McpAccessLevel.ReadOnly : McpAccessLevel.None;
      return (await (await ApiService.UpdateMcpAccessLevel({ workspaceId: org.id, mcpAccessLevel: nextLevel })).json()).mcpAccessLevel;
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!org) {
      return;
    }

    try {
      const result = await updateMcpAccess(data.mcpEnabled);
      setOrg({ ...org, mcpAccessLevel: result });
      await queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(org.id) });
      toast.success('MCP settings updated successfully');
    } catch {
      toast.error('Failed to update MCP settings');
    }
  });

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      <Controller
        control={control}
        name="mcpEnabled"
        render={({ field }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">MCP access</div>
            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={isPending || !canEditWorkspaceSettings} />
          </Label>
        )}
      />
      <p className="text-secondary-text text-xs">
        Allow workspace members to connect AI tools (ChatGPT, Claude) to Gearbelt using the Model Context Protocol (MCP).
      </p>

      {canEditWorkspaceSettings && (
        <Button type="submit" variant="success" className="mt-2 w-fit" disabled={isPending || isSubmitting || !isDirty} loading={isPending}>
          Save changes
        </Button>
      )}
    </form>
  );
}

function SettingsSitemateStartSecurityPage() {
  const { org } = useUserStore();

  const { data: account, isLoading } = useQuery(getSitemateStartAccountQueryOptions({ accountId: org?.accountId }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Security</h1>
          {(isLoading || !account) && <SettingsFormSkeleton rows={1} />}
          {account && <SettingsSitemateStartSecurityForm account={account} />}
          {org && <McpAccessToggleForm />}
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsSitemateStartSecurityForm({ account }: { account: IAccount }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useUserStore();

  const formInstance = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ssoEnabled: account.ssoEnabled ?? false,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  const { mutateAsync: updateAccount } = useMutation({
    mutationFn: accountService.updateAccount,
    onSuccess: async () => {
      toast.success('Security settings updated successfully');
      await queryClient.invalidateQueries({
        queryKey: sitemateStartAccountKeys.detail(account._id),
      });
    },
    onError: () => {
      toast.error('Failed to update feature access');
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    await updateAccount({
      _id: account._id,
      ssoEnabled: data.ssoEnabled,
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="ssoEnabled"
        render={({ field }) => (
          <Label className="flex items-center gap-1">
            <div className="w-1/3 shrink-0">Single sign-on (SSO)</div>
            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!user?.roles.includes(PermissionRoles.SitemateSuperUser)} />
          </Label>
        )}
      />

      {user?.roles.includes(PermissionRoles.SitemateSuperUser) && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsSitemateStartSecurityPage;
